/** Create dialog for a notification subscription (react-hook-form + zod).
 *
 * The row IS the opt-in: creating one mints a per-subscription API_Credential,
 * then registers the callback with the vendor. Choices cascade: tenant → the
 * tenant's booking integrations whose restech supports subscription management
 * (integration capability `can_manage_notification_subscriptions`) → that
 * restech's accounts ("default" + slugs) → the restech's supported
 * notification types (types outside the `implemented` set get an
 * "observe only" hint — they can be recorded but never processed).
 */
import {
  Alert,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import * as integrationsApi from '@/api/integrations';
import * as subscriptionsApi from '@/api/notificationSubscriptions';
import type { SubscriptionDetail } from '@/api/notificationSubscriptions';
import { AppDialog } from '@/components/AppDialog';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { singlePageTenant } from '@/scope/pageTenants';

import { SUBSCRIPTION_TYPES } from './subscriptionFormat';

/** Select sentinel for the default account (its real slug is the empty
 *  string, which MUI Select treats as "no value"). */
const DEFAULT_ACCOUNT = '_default';

const FormSchema = z.object({
  restech: z.string().min(1, 'Pick a booking system'),
  account: z.string().min(1, 'Pick an account'),
  notification_type: z.string().min(1, 'Pick a notification type'),
  mode: z.enum(['log_only', 'process']),
  callback_url_override: z
    .string()
    .trim()
    .refine((v) => v === '' || v.startsWith('https://'), 'Must be an https:// URL'),
});

type FormValues = z.infer<typeof FormSchema>;

const EMPTY: FormValues = {
  restech: '',
  account: DEFAULT_ACCOUNT,
  notification_type: '',
  mode: 'log_only',
  callback_url_override: '',
};

interface Props {
  open: boolean;
  /** The page's operators (D9): the one is pre-selected, all are featured; see
   *  `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  onClose: () => void;
  /** Fired with the created row (status active OR create_failed). */
  onCreated: (row: SubscriptionDetail) => void;
}

export function SubscriptionCreateDialog({ open, pageTenants, onClose, onCreated }: Props) {
  const snackbar = useSnackbar();
  const qc = useQueryClient();

  // Tenant lives outside RHF (self-contained controlled picker, same as the
  // launch-policy dialog).
  const [tenant, setTenant] = useState<TenantPickerValue | null>(null);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(FormSchema), defaultValues: EMPTY });

  useEffect(() => {
    if (!open) return;
    reset(EMPTY);
    setTenant(singlePageTenant(pageTenants));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The static catalog carries the per-restech capabilities…
  const catalogQuery = useQuery({
    queryKey: ['integrations', 'catalog'],
    queryFn: () => integrationsApi.listCatalog(),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  // …the tenant's rows carry which restechs + accounts this operator has.
  const rowsQuery = useQuery({
    queryKey: ['integrations', 'rows', tenant?.id],
    queryFn: () => integrationsApi.listRows(tenant!.id),
    enabled: open && tenant !== null,
  });

  const capableByRestech = useMemo(() => {
    const m = new Map<string, integrationsApi.IntegrationCatalogEntry>();
    for (const entry of catalogQuery.data?.results ?? []) {
      if (entry.can_manage_notification_subscriptions) m.set(entry.id, entry);
    }
    return m;
  }, [catalogQuery.data]);

  const bookingRows = useMemo(
    () => (rowsQuery.data?.results ?? []).filter((r) => r.kind === 'booking' && r.restech),
    [rowsQuery.data],
  );

  /** Restechs this tenant has an integration for AND that support
   *  subscription management. */
  const restechOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: { id: string; name: string }[] = [];
    for (const row of bookingRows) {
      const entry = capableByRestech.get(row.restech!);
      if (!entry || seen.has(entry.id)) continue;
      seen.add(entry.id);
      out.push({ id: entry.id, name: entry.name });
    }
    return out;
  }, [bookingRows, capableByRestech]);

  const restech = watch('restech');
  const mode = watch('mode');

  const accountOptions = useMemo(
    () =>
      bookingRows
        .filter((r) => r.restech === restech)
        .map((r) => ({
          value: r.is_default || !r.slug ? DEFAULT_ACCOUNT : r.slug,
          label: r.is_default || !r.slug ? 'default' : r.slug,
          displayName: r.display_name,
        })),
    [bookingRows, restech],
  );

  const catalogEntry = restech ? capableByRestech.get(restech) : undefined;
  const supportedTypes = catalogEntry?.notification_types?.supported ?? SUBSCRIPTION_TYPES;
  const implementedTypes = useMemo(
    () => new Set(catalogEntry?.notification_types?.implemented ?? []),
    [catalogEntry],
  );

  // Cascading resets: a new tenant invalidates the restech; a new restech
  // invalidates the account + type.
  const tenantId = tenant?.id ?? null;
  useEffect(() => {
    setValue('restech', '');
    setValue('account', DEFAULT_ACCOUNT);
    setValue('notification_type', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);
  useEffect(() => {
    setValue('account', accountOptions[0]?.value ?? DEFAULT_ACCOUNT);
    setValue('notification_type', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restech]);

  const mutation = useMutation({
    mutationFn: (body: subscriptionsApi.SubscriptionCreate) => subscriptionsApi.create(body),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['subscriptions'] });
      if (row.status === 'create_failed') {
        snackbar.show(
          'Subscription created, but the vendor registration failed — retry it from the detail view.',
          'warning',
        );
      } else {
        snackbar.show('Subscription created.', 'success');
      }
      onCreated(row);
      onClose();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Create failed.', 'error'),
  });

  const submit = handleSubmit((v) => {
    if (!tenant) {
      snackbar.show('Pick a tenant first.', 'warning');
      return;
    }
    mutation.mutate({
      tour_operator: tenant.id,
      restech: v.restech,
      account: v.account === DEFAULT_ACCOUNT ? '' : v.account,
      notification_type: v.notification_type,
      mode: v.mode,
      ...(v.callback_url_override ? { callback_url_override: v.callback_url_override } : {}),
    });
  });

  const noCapableIntegrations =
    tenant !== null && rowsQuery.isSuccess && catalogQuery.isSuccess && restechOptions.length === 0;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="sm"
      title="New notification subscription"
      disableBackdropClose
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={mutation.isPending || !tenant}
            onClick={submit}
          >
            {mutation.isPending ? 'Creating…' : 'Create subscription'}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }} component="form" onSubmit={submit} noValidate>
        <TenantPicker
          value={tenant}
          featured={pageTenants}
          onChange={setTenant}
          label="Tenant"
          placeholder="Pick a tenant"
        />

        {noCapableIntegrations && (
          <Alert severity="info">
            This tenant has no booking integration that supports notification subscriptions.
          </Alert>
        )}

        <Controller
          control={control}
          name="restech"
          render={({ field }) => (
            <TextField
              {...field}
              select
              size="small"
              label="Booking system"
              disabled={!tenant || restechOptions.length === 0}
              error={Boolean(errors.restech)}
              helperText={
                errors.restech?.message ??
                'Only integrations that support subscription management are listed.'
              }
            >
              {restechOptions.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <Controller
          control={control}
          name="account"
          render={({ field }) => (
            <TextField
              {...field}
              select
              size="small"
              label="Account"
              disabled={!restech || accountOptions.length === 0}
              error={Boolean(errors.account)}
              helperText={errors.account?.message ?? 'The integration account the subscription belongs to.'}
            >
              {accountOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                  {o.displayName && o.displayName !== o.label && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {o.displayName}
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <Controller
          control={control}
          name="notification_type"
          render={({ field }) => (
            <TextField
              {...field}
              select
              size="small"
              label="Notification type"
              disabled={!restech}
              error={Boolean(errors.notification_type)}
              helperText={
                errors.notification_type?.message ??
                '"Observe only" types are recorded but never processed — the import pipeline doesn’t implement them yet.'
              }
            >
              {supportedTypes.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                  {!implementedTypes.has(t) && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label="observe only"
                      sx={{ ml: 1, height: 18, fontSize: 11 }}
                    />
                  )}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <FormControl>
          <FormLabel id="subscription-mode-label" sx={{ fontSize: 13 }}>
            Delivery mode
          </FormLabel>
          <Controller
            control={control}
            name="mode"
            render={({ field }) => (
              <RadioGroup {...field} aria-labelledby="subscription-mode-label">
                <FormControlLabel value="log_only" control={<Radio size="small" />} label="Log only" />
                <FormControlLabel value="process" control={<Radio size="small" />} label="Process" />
              </RadioGroup>
            )}
          />
          <FormHelperText>
            The rollout ramp: start with log only — deliveries are authenticated and recorded but
            not processed — verify the recorded payloads, then switch to process.
          </FormHelperText>
        </FormControl>

        {mode === 'process' && (
          <Alert severity="warning">
            Processing is live from the first delivery — bookings, orders, products or availability
            update as notifications arrive.
          </Alert>
        )}

        <TextField
          {...register('callback_url_override')}
          size="small"
          label="Callback URL override (advanced)"
          placeholder="https://…"
          error={Boolean(errors.callback_url_override)}
          helperText={
            errors.callback_url_override?.message ??
            'Testing hook: register an arbitrary https URL (e.g. a webhook catcher) instead of the canonical callback. Shows as drift in the remote diff and in domain sync.'
          }
        />
      </Stack>
    </AppDialog>
  );
}
